/**
 * 已选房间模块
 */
import React, { useMemo, memo } from 'react';
import { connect } from 'react-redux';
import { Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import intl from 'react-intl-universal';

import ContentBlock from '@/components/Reservation/ContentBlock';
import CouponGapText from '@/components/Reservation/CouponGapText';
import InputWithCoupon from '@/components/Reservation/NumberInput/InputWithCoupon';
import { OccType } from '@/config/constant';
import { Calculator, parseFen2Yuan } from '@/utils/number';
import { renderContent } from '@/utils';
import { createStateSelector } from '@/redux/actions/reservation/helper';
import { ReducerState, storeName } from '@/redux/reducers/reservation/directRenewal';
import { RentRoomInfo } from '@/redux/reducers/reservation/base.d';
import { getOccSummaryText } from '@/pages/reservation/utils/formatter';

import styles from './index.module.scss';

const stateSelector = (state: ReducerState) => {
  const { rentRoomInfos, targetRoomMap } = state;
  return {
    rentRoomInfos,
    targetRoomMap,
  };
};
const mapStateToProps = createStateSelector<ReducerState>(storeName, stateSelector);
const mapDispatchToProps = {};

type MapStateProps = ReturnType<typeof stateSelector>;
type MapDispatchProps = typeof mapDispatchToProps;

const Index: React.FC<MapStateProps & MapDispatchProps> = (props) => {
  const { rentRoomInfos, targetRoomMap } = props;

  const renderGuestName = (name) => {
    return <div className={styles.wrapText}>{renderContent(name)}</div>;
  };

  const renderPriceItem = (dayPrice) => {
    return (
      <InputWithCoupon
        value={parseFen2Yuan(dayPrice.price)}
        inputDisabled
        couponDisabled
        couponValue={dayPrice.couponValue}
      />
    );
  };

  const formatDataSource = () => {
    const groupList: { roomTypeId: number; roomList: RentRoomInfo[] }[] = [];
    const depositValue = new Calculator(0); // 总押金
    let occType = OccType.DayUse; // 入住类型
    let occDuration = 0; // 入住时长;
    const dayPriceMap: Record<string, Calculator> = {}; // 天-总房价
    const priceValue = new Calculator(0); // 总房费
    const guestIdMap: Record<string, boolean> = {}; // 入住人
    rentRoomInfos.forEach((roomItem) => {
      const { roomTypeId, occInfoList } = roomItem as any;
      // 同一房型排一起
      let groupItem = groupList.find((group) => group.roomTypeId === roomTypeId);
      if (!groupItem) {
        groupItem = { roomTypeId, roomList: [] };
        groupList.push(groupItem);
      }
      const targetRoom = targetRoomMap[roomItem.targetId];
      groupItem.roomList.push({
        ...roomItem,
        roomPriceList: targetRoom.roomPriceList,
      });
      // 押金累计
      depositValue.plus(roomItem.deposit || 0);
      // 入住类型 入住时长取值
      occType = targetRoom.occType;
      occDuration = targetRoom.occDuration;
      // 单日、总房费
      targetRoom.roomPriceList.forEach((priceItem) => {
        const { rentDate, price } = priceItem as any;
        const roomPrice = dayPriceMap[rentDate] || new Calculator(0);
        roomPrice.plus(price || 0);
        priceValue.plus(price || 0);
        dayPriceMap[rentDate] = roomPrice;
      });
      // 宾客数量
      (occInfoList || []).forEach((guestItem) => {
        guestIdMap[guestItem.wyuId] = true;
      });
    });
    let tableData = [];
    groupList.forEach((groupItem) => {
      tableData = tableData.concat(groupItem.roomList);
    });
    return {
      listData: tableData,
      occType,
      occDuration,
      totalDeposit: depositValue.getValue(),
      dayPriceMap,
      totalPrice: priceValue.getValue(),
      guestCount: Object.keys(guestIdMap).length,
    };
  };

  const tableInfo = useMemo(() => {
    const fixedLeftColumns: ColumnsType<RentRoomInfo> = [
      {
        title: intl.get('app_constants_room_type'),
        dataIndex: 'roomTypeName',
        key: 'roomTypeName',
        fixed: 'left',
        width: 200,
      },
      {
        title: intl.get('app_constants_room_no'),
        dataIndex: 'roomNo',
        key: 'roomNo',
        fixed: 'left',
        width: 140,
      },
      {
        title: intl.get('app_constants_occupant_name'),
        dataIndex: 'guestNames',
        key: 'guestNames',
        width: 200,
        render: renderGuestName,
      },
      {
        title: intl.get('app_reservation_deposit_amount'),
        dataIndex: 'deposit',
        key: 'roomNo',
        width: 100,
        render: parseFen2Yuan,
      },
    ];
    const rentDayColumns: ColumnsType<RentRoomInfo> = [];
    const rentDaySummary = []; // 日期总计栏
    let cellIndex = fixedLeftColumns.length; // 总计栏下标=左侧固定列长度
    const {
      listData,
      guestCount,
      occType,
      occDuration,
      totalDeposit,
      dayPriceMap,
      totalPrice,
    } = formatDataSource();
    if (listData.length) {
      const { roomPriceList } = listData[0]; // 入住天价格列表
      const rentDays = roomPriceList || [];
      rentDays.forEach((rentDay, idx) => {
        const { rentDate } = rentDay;
        rentDayColumns.push({
          title: <CouponGapText text={rentDate} />,
          dataIndex: ['roomPriceList', idx],
          width: 260,
          align: 'right',
          render: renderPriceItem,
        });
        const dayPrice = dayPriceMap[rentDate]; // 当天房间总价
        rentDaySummary.push(
          <Table.Summary.Cell index={cellIndex} key={cellIndex} align="right">
            <CouponGapText text={parseFen2Yuan(dayPrice.getValue())} />
          </Table.Summary.Cell>,
        );
        cellIndex += 1;
      });
    }
    return {
      listData,
      tableColumns: [...fixedLeftColumns, ...rentDayColumns],
      tableSummary: [
        <Table.Summary.Cell index={0} key="0">
          {intl.get('reservation_room_summary_label')}
        </Table.Summary.Cell>,
        <Table.Summary.Cell index={1} key="1">
          {listData.length}
        </Table.Summary.Cell>,
        <Table.Summary.Cell index={2} key="2">
          {guestCount}
        </Table.Summary.Cell>,
        <Table.Summary.Cell index={3} key="3">
          {parseFen2Yuan(totalDeposit)}
        </Table.Summary.Cell>,
        ...rentDaySummary,
      ],
      tableFooter: getOccSummaryText({
        count: listData.length,
        type: occType,
        duration: occDuration,
        amount: parseFen2Yuan(totalPrice),
      }),
    };
  }, [rentRoomInfos, targetRoomMap]);

  return (
    <ContentBlock title={intl.get('drawer_extension_room_block_title')}>
      <Table
        scroll={{ x: 600 }}
        pagination={false}
        className={styles.rentRoomContainer}
        rowKey="indexId"
        columns={tableInfo.tableColumns}
        dataSource={tableInfo.listData}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row className={styles.tableSummary}>
              {tableInfo.tableSummary}
            </Table.Summary.Row>
          </Table.Summary>
        )}
        footer={() => tableInfo.tableFooter}
      />
    </ContentBlock>
  );
};

export default connect<MapStateProps, MapDispatchProps>(
  mapStateToProps,
  mapDispatchToProps,
)(memo(Index));
